import moment from 'moment'

// LOGIKA BIZNESOWA:
// Rating płatniczy (0–5 gwiazdek) ocenia wiarygodność klienta na podstawie 3 wymiarów:
//   1. Średnie opóźnienie płatności (45 pkt) — jak szybko klient reguluje zobowiązania.
//   2. LICZBA faktur przeterminowanych (35 pkt) — ile zobowiązań klient obecnie "ciągnie".
//   3. Kwota przeterminowana (20 pkt) — łączna wartość zaległości.
//
// Większą wagę dajemy LICZBIE przeterminowanych faktur niż samej kwocie, bo:
//   - jedna duża zaległa faktura (np. 200 000 PLN) to zazwyczaj chwilowe, uzgodnione
//     opóźnienie u dużego klienta — nadal ma on dobrą historię regulacji,
//   - dziesięć zaległych faktur po 20 000 PLN pokazuje chroniczne problemy
//     z regulowaniem należności — nawet jeśli sumarycznie jest to tyle samo.
// Dzięki tej wadze klient z 1 fakturą na 200k PLN dostanie wyższy rating niż klient
// z 10 fakturami łącznie na 50k, co odpowiada realnemu ryzyku windykacyjnemu.

const MS_PER_DAY = 24 * 60 * 60 * 1000

const startOfDay = (d) => {
  const date = d instanceof Date ? d : new Date()
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

const diffDays = (from, to) => {
  if (!(from instanceof Date) || !(to instanceof Date)) return 0
  return Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY)
}

// Korekty (numer zaczyna się od "KOR") nie wchodzą do ratingu płatniczego — odnoszą się do innych faktur
// i często mają własny, niezależny harmonogram płatności/zwrotu, który zaburzałby ocenę terminowości.
const isCorrectionInvoice = (invoice) => {
  const name = invoice && invoice.invoiceName
  if (typeof name !== 'string') return false
  return name.trim().toUpperCase().startsWith('KOR')
}

const parseDueDate = (invoice) => {
  if (!invoice || !invoice.paymentDate) return null
  const m = moment(invoice.paymentDate, 'DD.MM.YYYY', true)
  if (!m.isValid()) return null
  return m.startOf('day').toDate()
}

const parsePaidDate = (invoice) => {
  if (!invoice) return null
  if (typeof invoice.paidDate === 'number' && !Number.isNaN(invoice.paidDate)) {
    return new Date(invoice.paidDate * 1000)
  }
  if (Array.isArray(invoice.deposits) && invoice.deposits.length > 0) {
    let latest = null
    for (const d of invoice.deposits) {
      if (d && typeof d.date === 'number' && (latest === null || d.date > latest)) {
        latest = d.date
      }
    }
    if (typeof latest === 'number') return new Date(latest * 1000)
  }
  return null
}

export const computeInvoiceStats = (invoicesRaw, nowDate) => {
  const today = startOfDay(nowDate)
  const invoices = Array.isArray(invoicesRaw)
    ? invoicesRaw.filter((i) => i && i.active !== false && !isCorrectionInvoice(i))
    : []

  let unpaidInvoicesCount = 0
  let overdueInvoicesCount = 0
  let overdueAmount = 0
  const delaySamples = []

  for (const inv of invoices) {
    const due = parseDueDate(inv)
    const isPaid = inv.paid === true

    if (!isPaid) {
      unpaidInvoicesCount++
      if (due && due.getTime() < today.getTime()) {
        overdueInvoicesCount++
        const value = Number(inv.valueOfInvoice) || 0
        const paidAmount = Number(inv.paidAmount) || 0
        const outstanding = value - paidAmount
        // Ujemne wartości (nadpłaty / korekty) traktujemy jako 0 zaległości.
        if (outstanding > 0) overdueAmount += outstanding
        delaySamples.push(Math.max(0, diffDays(due, today)))
      }
    } else {
      const paidAt = parsePaidDate(inv)
      if (due && paidAt) {
        delaySamples.push(Math.max(0, diffDays(due, paidAt)))
      }
    }
  }

  overdueAmount = Math.round(overdueAmount * 100) / 100

  const averageDelayDays = delaySamples.length > 0
    ? delaySamples.reduce((a, b) => a + b, 0) / delaySamples.length
    : 0

  return {
    averageDelayDays,
    overdueInvoicesCount,
    overdueAmount,
    unpaidInvoicesCount,
    totalInvoicesCount: invoices.length
  }
}

export const scoreDelay = (avgDays) => {
  const d = Number(avgDays) || 0
  if (d <= 3) return 45
  if (d <= 7) return 38
  if (d <= 14) return 28
  if (d <= 30) return 15
  if (d <= 60) return 5
  return 0
}

export const scoreOverdueCount = (count) => {
  const c = Math.max(0, Math.floor(Number(count) || 0))
  if (c === 0) return 35
  if (c === 1) return 30
  if (c <= 3) return 24
  if (c <= 6) return 16
  if (c <= 10) return 8
  return 0
}

export const scoreOverdueAmount = (amount) => {
  const a = Math.max(0, Number(amount) || 0)
  if (a === 0) return 20
  if (a <= 10000) return 16
  if (a <= 50000) return 12
  if (a <= 200000) return 7
  if (a <= 500000) return 3
  return 0
}

export const scoreToStars = (score) => {
  const s = Number(score) || 0
  if (s >= 90) return 5
  if (s >= 75) return 4
  if (s >= 55) return 3
  if (s >= 35) return 2
  if (s >= 15) return 1
  return 0
}

export const computePaymentRating = (invoices, nowDate) => {
  const stats = computeInvoiceStats(invoices, nowDate)

  // Klient z samymi korekturami lub bez faktur wcale — nie przyznajemy ratingu, bo zerowe statystyki
  // po wyfiltrowaniu dałyby fałszywe 5/5 (0 opóźnień + 0 przeterminowanych + 0 kwoty = 100 pkt).
  if (stats.totalInvoicesCount === 0) {
    return {
      hasHistory: false,
      averageDelayDays: 0,
      overdueInvoicesCount: 0,
      overdueAmount: 0,
      unpaidInvoicesCount: 0,
      totalInvoicesCount: 0,
      paymentRatingScore: null,
      paymentRatingStars: null
    }
  }

  const delayScore = scoreDelay(stats.averageDelayDays)
  const overdueCountScore = scoreOverdueCount(stats.overdueInvoicesCount)
  const overdueAmountScore = scoreOverdueAmount(stats.overdueAmount)
  const totalScore = delayScore + overdueCountScore + overdueAmountScore
  const paymentRatingStars = scoreToStars(totalScore)

  return {
    hasHistory: true,
    averageDelayDays: stats.averageDelayDays,
    overdueInvoicesCount: stats.overdueInvoicesCount,
    overdueAmount: stats.overdueAmount,
    unpaidInvoicesCount: stats.unpaidInvoicesCount,
    totalInvoicesCount: stats.totalInvoicesCount,
    paymentRatingScore: totalScore,
    paymentRatingStars
  }
}

export const computeDaysLateForInvoice = (invoice, nowDate) => {
  const today = startOfDay(nowDate)
  const due = parseDueDate(invoice)
  const isPaid = invoice?.paid === true

  if (!due) {
    return { daysLate: 0, wasLate: false, isOverdue: false, paid: isPaid }
  }

  if (isPaid) {
    const paidAt = parsePaidDate(invoice)
    if (paidAt) {
      const days = Math.max(0, diffDays(due, paidAt))
      return { daysLate: days, wasLate: days > 0, isOverdue: false, paid: true }
    }
    return { daysLate: 0, wasLate: false, isOverdue: false, paid: true }
  }

  const isOverdue = due.getTime() < today.getTime()
  const days = isOverdue ? Math.max(0, diffDays(due, today)) : 0
  return { daysLate: days, wasLate: isOverdue, isOverdue, paid: false }
}

export const formatAmountPLN = (amount) => {
  const v = Number.isFinite(Number(amount)) ? Number(amount) : 0
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(v) + ' PLN'
}

export const formatDaysLabel = (days) => {
  const n = Math.abs(Math.round(Number(days) || 0))
  if (n === 1) return '1 dzień'
  return `${n} dni`
}

export const formatAverageDelay = (days) => {
  const d = Number(days)
  if (!Number.isFinite(d)) return '—'
  const rounded = Math.round(d * 10) / 10
  if (rounded === 1) return '1 dzień'
  const display = Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1).replace('.', ',')
  return `${display} dni`
}
